import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingState } from "@/components/data/LoadingState";
import { ErrorState } from "@/components/data/ErrorState";
import { EmptyState } from "@/components/data/EmptyState";
import { JsonViewer } from "@/components/data/JsonViewer";
import { Badge } from "@/components/ui/badge";
import { RelTime } from "@/components/data/RelTime";
import { useMessages, paginatedItems } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { MessageRow } from "@/types";

const PAGE = 50;

export function MessagesPage() {
  const [channel, setChannel] = useState<"all" | "telegram" | "whatsapp">("all");
  const [page, setPage] = useState(0);
  const q = useMessages({
    limit: PAGE,
    offset: page * PAGE,
    channel: channel === "all" ? undefined : channel,
  });

  const rows = paginatedItems<MessageRow>(q.data);
  const filtered = useMemo(() => (channel === "all" ? rows : rows.filter((m) => m.channel === channel)), [rows, channel]);
  const [selected, setSelected] = useState<MessageRow | null>(null);
  const sel = selected ?? filtered[0] ?? null;

  return (
    <>
      <PageHeader title="Messages" description="System-wide stream of inbound messages from Telegram and WhatsApp." />

      <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr),minmax(0,3fr)]">
        <Card className="overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle>Stream</CardTitle>
            <div className="flex gap-1">
              {(["all", "telegram", "whatsapp"] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => {
                    setChannel(c);
                    setPage(0);
                  }}
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-[11px] capitalize transition-colors",
                    channel === c ? "bg-foreground text-background" : "bg-secondary/60 text-muted-foreground hover:text-foreground"
                  )}
                >
                  {c}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[calc(100vh-280px)] overflow-y-auto scrollbar-thin">
              {q.isLoading ? (
                <LoadingState />
              ) : q.isError ? (
                <ErrorState error={q.error} onRetry={() => q.refetch()} />
              ) : filtered.length === 0 ? (
                <EmptyState title="No messages" />
              ) : (
                <ul className="divide-y divide-border/40">
                  {filtered.map((m) => (
                    <li key={String(m.id)}>
                      <button
                        onClick={() => setSelected(m)}
                        className={cn(
                          "w-full px-4 py-2.5 text-left transition-colors hover:bg-secondary/40",
                          sel?.id === m.id && "bg-secondary/60"
                        )}
                      >
                        <div className="flex items-center gap-2 text-[11px]">
                          <Badge variant={m.channel === "telegram" ? "info" : "success"} className="capitalize">
                            {m.channel}
                          </Badge>
                          <Badge variant="outline" className="capitalize">{m.direction}</Badge>
                          {m.user_id && (
                            <Link to={`/users/${m.user_id}`} className="font-mono text-sky-300 hover:underline" onClick={(e) => e.stopPropagation()}>
                              {m.user_id.slice(0, 8)}
                            </Link>
                          )}
                          <span className="ml-auto text-muted-foreground">
                            <RelTime value={m.created_at} />
                          </span>
                        </div>
                        <div className="mt-1 line-clamp-2 text-xs">{m.text || <span className="text-muted-foreground">(no text)</span>}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-border/40 px-3 py-2 text-xs text-muted-foreground">
              <span className="tabular-nums">{rows.length} loaded</span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </Button>
                <span className="px-2 tabular-nums">page {page + 1}</span>
                <Button size="sm" variant="ghost" disabled={rows.length < PAGE} onClick={() => setPage((p) => p + 1)}>
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Detail</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!sel ? (
              <EmptyState title="Select a message" />
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Badge variant={sel.channel === "telegram" ? "info" : "success"} className="capitalize">{sel.channel}</Badge>
                  <Badge variant="outline" className="capitalize">{sel.direction}</Badge>
                  {sel.user_id && (
                    <Link to={`/users/${sel.user_id}`} className="font-mono text-sky-300 hover:underline">
                      {sel.user_id}
                    </Link>
                  )}
                  <span className="ml-auto text-muted-foreground"><RelTime value={sel.created_at} /></span>
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Inbound</div>
                  <div className="rounded-md border border-border/50 bg-background/40 p-3 font-mono text-xs whitespace-pre-wrap">
                    {sel.text || <span className="text-muted-foreground">—</span>}
                  </div>
                </div>
                {sel.ai_response && (
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">AI response</div>
                    <div className="rounded-md border border-sky-500/20 bg-sky-500/5 p-3 font-mono text-xs whitespace-pre-wrap">
                      {sel.ai_response}
                    </div>
                  </div>
                )}
                {sel.expense_id && <Badge variant="info">expense #{sel.expense_id}</Badge>}
                {sel.error && (
                  <div className="rounded-md border border-rose-500/30 bg-rose-500/10 p-3 font-mono text-xs text-rose-200">{sel.error}</div>
                )}
                {sel.raw != null && <JsonViewer value={sel.raw} />}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
